import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { t } from '@luna/i18n';

import type { PhoneNumber } from '../ContactTypes';
import { usePhoneNumbers } from '../usePhoneNumbers';
import type { AllPhoneNumbersFormatted } from './PhoneNumberSelectService';
import {
    convertPhoneNumbersToSelectItems,
    getSelectPhoneNumbers,
} from './PhoneNumberSelectService';
import { isPhoneNumberEntryAllowed } from '../PhoneNumbersService';
import { useContactListFlags } from '../useContacts';
import { ContactSelect } from './ContactSelect';
import type { Contact } from '../ContactDataManagementTypes';

export type PhoneNumberSelectProps = {
    addValueToItemsIfNotIncluded?: boolean;
    preselectPrimaryNumber?: boolean;
    backUrl?: string;
    mobileNumbersOnly?: boolean;
    menuClassName?: string;
    name?: string;
    placeholder?: string;
    phoneNumberId: string | undefined;
    onSelect: (phoneNumbers: PhoneNumber[]) => void;
    filterContacts?: (contacts: Contact[]) => Contact[];
};

type Props = PhoneNumberSelectProps & {
    addNewPhoneNumberHref?: string;
    onAddNewPhoneNumberClick?(): void;
};

export const SharedPhoneNumberSelect: FC<Props> = ({
    phoneNumberId,
    addValueToItemsIfNotIncluded,
    preselectPrimaryNumber,
    backUrl,
    mobileNumbersOnly = false,
    addNewPhoneNumberHref,
    onAddNewPhoneNumberClick,
    filterContacts,
    ...props
}) => {
    const { flags } = useContactListFlags();
    const [{ phoneNumbers, isLoading, hasError }, fetchContacts] = usePhoneNumbers({
        mobileNumbersOnly,
        filterContacts,
    });

    const [{ isInitializing, allPhoneNumbers }, setAllPhoneNumbers] =
        useState<AllPhoneNumbersFormatted>({
            isInitializing: true,
            allPhoneNumbers: [],
        });

    useEffect(() => {
        if (isLoading) {
            setAllPhoneNumbers({ isInitializing: true, allPhoneNumbers: [] });
            return;
        }

        const setAllPhoneNumbersAsync = async () => {
            const numbers = await getSelectPhoneNumbers(
                phoneNumbers,
                phoneNumberId,
                addValueToItemsIfNotIncluded,
            );
            setAllPhoneNumbers({ isInitializing: false, allPhoneNumbers: numbers });
        };
        setAllPhoneNumbersAsync();
    }, [isLoading, phoneNumberId, phoneNumbers, addValueToItemsIfNotIncluded]);

    return (
        <ContactSelect
            value={phoneNumberId}
            contacts={allPhoneNumbers}
            isLoading={isInitializing}
            hasError={hasError}
            isNewContactAllowed={isPhoneNumberEntryAllowed(mobileNumbersOnly, flags)}
            invalidContactText={t('contactDataManagement.selectedPhoneNumberIsNotValidWarning')}
            invalidContactBtnLabel={t('contactDataManagement.managePhoneNumbersLink')}
            preselectPrimaryContact={preselectPrimaryNumber}
            addNewContactHref={addNewPhoneNumberHref}
            onAddNeContactClick={onAddNewPhoneNumberClick}
            convertContactsToSelectItems={convertPhoneNumbersToSelectItems}
            fetchContacts={fetchContacts}
            {...props}
        />
    );
};
